/**
 * COMMS.app's feed filter — the pure half, kept out of {@link CommsApp} so the derivation is
 * unit-testable without mounting React. The menu that drives it is `CommsFilterMenu`.
 *
 * The options are **and-able checkboxes**, not a single-select row. They describe what a person is
 * reading, rather than leaking the transport's `FeedEntry.kind`: Messages / Dice rolls / Combat
 * results / Activity log, each with a one-line description of what it routes. There is
 * deliberately no `all` pseudo-kind — "everything" is just all four checked — and unchecking
 * everything is allowed: it shows an empty feed with a link back to the defaults. Selection is
 * session-local UI state, never persisted.
 *
 * Not every entry rides an option, though — {@link feedVisibility} returns a **three-outcome**
 * result, not an option-or-nothing binary. Presence/debug events (`"join"`/`"leave"`) are
 * `"hidden"`: deliberately never shown, by name, no option can reveal them. A private refusal
 * (`"roll_error"` and its GM-only-gate sibling `"refused"`) is `"always"`: shown
 * unconditionally, regardless of selection — a player's own refusal feedback must not be
 * filterable off, since unchecking everything is deliberately allowed. Collapsing
 * "deliberately hidden" and "always shown" into one `undefined` fallthrough is the bug this
 * three-way split exists to prevent: it's how a REAL event (`roll_error`) silently vanished
 * alongside the intentionally-hidden ones. An **emote** rides Messages — it's player-authored
 * table conversation, not an unfilterable refusal and not presence noise, so a reader who's
 * turned Messages off doesn't want it either.
 */
import type { FeedEntry, SystemFeedEvent } from "@lancer-vtt/shared";

/** The player-facing categories, in menu order. */
export const FEED_FILTER_KINDS = ["chat", "roll", "combat", "activity"] as const;
export type FeedFilterKind = (typeof FEED_FILTER_KINDS)[number];

/**
 * The menu's label and description per kind. Each description names what {@link feedVisibility}
 * actually routes there, so a change to that routing is a change to this copy.
 */
export const FEED_FILTER_OPTIONS: Record<FeedFilterKind, { label: string; description: string }> = {
  chat: { label: "Messages", description: "What people type, and emotes." },
  roll: { label: "Dice rolls", description: "Every dice roll and its total." },
  combat: { label: "Combat results", description: "Attacks, saves, damage, structure checks, reaction choices, and undos." },
  activity: { label: "Activity log", description: "Who did what: moves, activations, turns and rounds, HP and status edits, drawings, map setup." },
};

/**
 * What RESET restores: conversation, dice, and the fight. The activity log is useful context but
 * not the running combat story, so it is opt-in.
 */
export const DEFAULT_FEED_FILTER: readonly FeedFilterKind[] = ["chat", "roll", "combat"];

/** A fresh default selection set — a new `Set` per call, since it becomes mutable React state. */
export function defaultFeedFilter(): Set<FeedFilterKind> {
  return new Set(DEFAULT_FEED_FILTER);
}

/** Flip one kind on/off, returning a new set (never mutating the current state value). */
export function toggleFeedFilter(selected: ReadonlySet<FeedFilterKind>, kind: FeedFilterKind): Set<FeedFilterKind> {
  const next = new Set(selected);
  if (next.has(kind)) next.delete(kind);
  else next.add(kind);
  return next;
}

/** True when a selection is exactly the defaults (RESET has nothing to do). */
export function isDefaultFeedFilter(selected: ReadonlySet<FeedFilterKind>): boolean {
  return selected.size === DEFAULT_FEED_FILTER.length && DEFAULT_FEED_FILTER.every((k) => selected.has(k));
}

/**
 * The short label beside the filter icon: null at the defaults, else how many kinds are hidden
 * ("SHOWING ALL" when none are).
 */
export function feedFilterBadge(selected: ReadonlySet<FeedFilterKind>): string | null {
  if (isDefaultFeedFilter(selected)) return null;
  const hidden = FEED_FILTER_KINDS.filter((k) => !selected.has(k)).length;
  if (hidden === 0) return "SHOWING ALL";
  return hidden === 1 ? "1 TYPE HIDDEN" : `${hidden} TYPES HIDDEN`;
}

/**
 * Where an entry sits relative to the filter: a filterable category, always-shown, or never
 * shown.
 */
export type FeedVisibility = FeedFilterKind | "always" | "hidden";

/**
 * The visibility of a feed entry. Chat/roll entries ride their own kind directly; system
 * events switch **exhaustively** on {@link SystemFeedEvent} — the `never`-typed default arm
 * makes adding a sixth event to that union (`shared/src/index.ts`) a compile error here rather
 * than a silent fallthrough, which is the failure mode that dropped `roll_error` in the first
 * place.
 */
export function feedVisibility(entry: FeedEntry): FeedVisibility {
  if (entry.kind === "chat") return "chat";
  if (entry.kind === "roll") return "roll";
  if (entry.kind === "emote") return "chat";
  switch (entry.event) {
    case "resolution":
    case "decision":
    // An undo/redo line sits beside the cards it strikes.
    case "undo":
    case "redo":
      return "combat";
    // Every applied encounter command that isn't a resolution (`encounterRoomCore.onApplied`).
    case "command":
      return "activity";
    case "roll_error":
    case "refused":
      return "always";
    case "join":
    case "leave":
      return "hidden";
    default: {
      const _exhaustive: never = entry.event;
      return _exhaustive;
    }
  }
}

/** The entries a selection shows. An empty selection shows only the always-visible entries. */
export function filterFeed(feed: readonly FeedEntry[], selected: ReadonlySet<FeedFilterKind>): FeedEntry[] {
  return feed.filter((entry) => {
    const visibility = feedVisibility(entry);
    if (visibility === "always") return true;
    if (visibility === "hidden") return false;
    return selected.has(visibility);
  });
}
